import { useEffect, useState } from 'react';
import { usePageContent } from '@/context/SiteDataContext';
import api from '@/lib/api';
import AdminHint from '@/components/editable/AdminHint';

/**
 * BioPage
 * -------
 * Generic single-person bio template used by /about/prelate, /about/supervisor,
 * /about/first-lady, and any /legacy/<slug> profiles.
 * Content model (stored in page_content[slug]):
 *   { photo, name, title, subtitle, bio, bio_paragraphs: [str],
 *     youtube_url, show_video: boolean }
 * If `show_video` is false or `youtube_url` is empty the video section is hidden.
 */
export default function BioPage({ slug, defaults = {} }) {
  const c = usePageContent(slug);
  const merged = { ...defaults, ...c };
  const paragraphs = (merged.bio_paragraphs && merged.bio_paragraphs.length)
    ? merged.bio_paragraphs
    : (merged.bio ? [merged.bio] : []);
  const showVideo = merged.show_video !== false && merged.youtube_url;
  const embed = showVideo ? toYouTubeEmbed(merged.youtube_url) : null;

  return (
    <div>
      <section className="relative py-24 bg-royal-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          {merged.hero_image && <img src={merged.hero_image} alt="" className="w-full h-full object-cover opacity-30" />}
          <div className="absolute inset-0 bg-gradient-to-b from-royal/80 to-royal-dark/95" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-2xl ring-4 ring-gold/40 bg-royal/20">
              {merged.photo && (
                <img
                  src={merged.photo}
                  alt={merged.name || ''}
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: merged.photo_position || 'center top',
                    transform: merged.photo_scale ? `scale(${merged.photo_scale})` : undefined,
                    transformOrigin: 'center top',
                  }}
                />
              )}
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="overline text-gold mb-3">{merged.overline || 'Jurisdictional Leadership'}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold gold-underline">{merged.name || defaults.name || 'Name'}</h1>
            <div className="mt-3 text-xl text-gold">{merged.title || defaults.title}</div>
            {merged.subtitle && <div className="mt-1 text-white/70 italic">{merged.subtitle}</div>}
          </div>
        </div>
      </section>
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-6 text-charcoal-muted text-lg leading-relaxed">
          {paragraphs.length === 0
            ? <AdminHint className="italic text-center py-8">Add a biography in Admin ▸ Bio Pages ▸ {defaults.name || slug}.</AdminHint>
            : paragraphs.map((p, i) => <p key={i} className="whitespace-pre-wrap">{p}</p>)}
        </div>
      </section>
      {embed && (
        <section className="bg-[#F8F9FA] py-16" data-testid={`bio-video-${slug}`}>
          <div className="max-w-4xl mx-auto px-6">
            <div className="overline mb-3">Watch</div>
            <h3 className="font-serif text-2xl text-royal font-bold mb-6 gold-underline">{merged.video_title || 'A Word From the Ministry'}</h3>
            <div className="aspect-video rounded-xl overflow-hidden shadow-2xl ring-2 ring-gold/40 bg-black">
              <iframe
                src={embed}
                title="Ministry video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// Convert any common YouTube URL (watch?v=…, youtu.be/…, /shorts/…) to /embed/…
export function toYouTubeEmbed(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${u.pathname.replace('/', '')}`;
    }
    if (u.pathname.startsWith('/embed/')) return url;
    if (u.pathname.startsWith('/shorts/')) {
      return `https://www.youtube.com/embed/${u.pathname.replace('/shorts/', '')}`;
    }
    const id = u.searchParams.get('v');
    if (id) return `https://www.youtube.com/embed/${id}`;
    return url;
  } catch {
    return url;
  }
}

export function PrelatePage()    { return <BioPage slug="prelate-bio"     defaults={{ name: 'The Prelate',    title: 'Bishop · Jurisdictional Prelate' }} />; }
export function SupervisorPage() { return <BioPage slug="supervisor-bio"  defaults={{ name: 'The Supervisor', title: 'Jurisdictional Supervisor of Women' }} />; }
export function FirstLadyPage()  { return <BioPage slug="first-lady-bio"  defaults={{ name: 'The First Lady', title: 'First Lady of the Jurisdiction' }} />; }
